import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';

import AddIcon from '@mui/icons-material/Add';

import Button from '@shared/atoms/Button';
import SearchField from '@shared/atoms/SearchField';
import Snackbar from '@shared/atoms/Snackbar';
import T from '@shared/atoms/Typography';
import Dialog from '@shared/molecules/Dialog';

import theme from '@shared/themes/default';
import { newSelectTranslation, logger } from '@shared/utils';

import { useAppGlobals } from '../../contexts/AppContext';

import CategoriesList from './components/CategoriesList';
import CategoryDrawer from './components/CategoryDrawer';
import {
  createCategory,
  deleteCategory,
  updateCategory,
} from './graphql/mutations';
import { listCategories } from './graphql/queries';

const SearchWrapper = styled.div`
  column-gap: ${theme.spacing.s};
  display: flex;
`;

const OuterWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.m1};
  max-width: 650px;
`;

const CategoriesView = () => {
  const [categories, setCategories] = useState([]);
  const [categoriesFiltered, setCategoriesFiltered] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [detailedErrors, setDetailedErrors] = useState();
  const [selectedCategory, setSelectedCategory] = useState();
  const [toDeleteCategory, setToDeleteCategory] = useState();
  const [deleteCategoryDialog, setDeleteCategoryDialog] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [displayError, setDisplayError] = useState('');
  const [isLocked, setIsLocked] = useState(true);
  const [showDrawerError, setShowDrawerError] = useState(false);

  const { locale, useNewGraphQLQuery, useGraphQLMutation } = useAppGlobals();
  const { t } = useTranslation();

  const [searchParams] = useSearchParams();
  const deepLinkedCategoryId = searchParams.get('category');
  const hasDeepLinkedRef = useRef(false);

  const listCategoriesResponse = useNewGraphQLQuery(listCategories, {
    variables: {
      pagination: { limit: 1000 },
    },
  });

  const handleDeleteCategoryDialog = (id) => {
    setToDeleteCategory(categories.find((category) => category.id === id));
    setDeleteCategoryDialog(true);
  };

  const handleCloseDrawer = () => {
    setSelectedCategory();
  };

  const [createCategoryMutation, createCategoryResponse] = useGraphQLMutation(
    createCategory,
    {
      update(cache, { data: { createCategory } }) {
        cache.writeQuery({
          query: listCategories,
          data: {
            listCategories: [
              ...categories,
              { ...createCategory, courses: [] },
            ].sort((a, b) => {
              if (a.ordinal < b.ordinal) return -1;
              if (a.ordinal > b.ordinal) return 1;
              return 0;
            }),
          },
          variables: {
            pagination: { limit: 1000 },
          },
        });
      },
      onCompleted() {
        handleCloseDrawer();
      },
      onError({ message }) {
        setShowDrawerError(true);
        if (message) {
          setDisplayError(t('backoffice.categories.errorSaving'));
          const detailedError = [
            {
              title: t('backoffice.categories.errorSaving'),
              description: message,
            },
          ];
          setDetailedErrors(detailedError);
        }
      },
    }
  );

  const [updateCategoryMutation, updateCategoryResponse] = useGraphQLMutation(
    updateCategory,
    {
      onCompleted({ updateCategory }) {
        const newCategoryIndex = categories.findIndex(
          (category) => category.id === updateCategory.id
        );
        setCategories(
          [
            ...categories.slice(0, newCategoryIndex),
            { ...updateCategory },
            ...categories.slice(newCategoryIndex + 1),
          ].sort((a, b) => {
            if (a.ordinal < b.ordinal) return -1;
            if (a.ordinal > b.ordinal) return 1;
            return 0;
          })
        );
        handleCloseDrawer();
      },
      onError({ message }) {
        setShowDrawerError(true);
        if (message) {
          setDisplayError(t('backoffice.categories.errorSaving'));
          const detailedError = [
            {
              title: t('backoffice.categories.errorSaving'),
              description: message,
            },
          ];
          setDetailedErrors(detailedError);
        }
      },
    }
  );

  const [deleteCategoryMutation, deleteCategoryResponse] = useGraphQLMutation(
    deleteCategory,
    {
      onCompleted({ deleteCategory }) {
        setCategories([
          ...categories.filter((category) => category.id !== deleteCategory.id),
        ]);
        setDeleteCategoryDialog(false);
        setIsLocked(false);
      },
      onError({ message }) {
        setDisplayError(message);
        setIsLocked(false);
      },
    }
  );

  const handleCreateCategory = async (input) => {
    try {
      setShowDrawerError(false);
      await createCategoryMutation({
        variables: {
          input,
        },
      });
    } catch (err) {
      logger.error(err);
    }
  };

  const handleUpdateCategory = async (input) => {
    try {
      setShowDrawerError(false);
      await updateCategoryMutation({
        variables: {
          input,
        },
      });
    } catch (err) {
      logger.error(err);
    }
  };

  const handleDeleteCategory = async () => {
    try {
      setIsLocked(true);
      await deleteCategoryMutation({
        variables: {
          id: toDeleteCategory.id,
        },
      });
    } catch (err) {
      logger.error(err);
    }
  };

  useEffect(() => {
    if (searchValue) {
      setCategoriesFiltered(
        categories.filter((category) =>
          newSelectTranslation(category.name, locale)
            .toLowerCase()
            .includes(searchValue.toLowerCase())
        )
      );
    } else {
      setCategoriesFiltered(categories);
    }
  }, [categories, searchValue]);

  useEffect(() => {
    const { loading, data, error } = listCategoriesResponse;
    if (!loading) {
      if (error) {
        setDisplayError(t('backoffice.categories.errorFetching'));
      }
      if (data?.listCategories) {
        setCategories(
          [...data.listCategories].sort((a, b) => {
            if (a.ordinal < b.ordinal) return -1;
            if (a.ordinal > b.ordinal) return 1;
            return 0;
          })
        );
      }
      setIsLocked(false);
    }
  }, [listCategoriesResponse]);

  useEffect(() => {
    setDrawerOpen(!!selectedCategory);
  }, [selectedCategory]);

  useEffect(() => {
    if (
      hasDeepLinkedRef.current ||
      !deepLinkedCategoryId ||
      !categories.length
    ) {
      return;
    }
    const match = categories.find(
      (category) => String(category.id) === deepLinkedCategoryId
    );
    if (match) {
      hasDeepLinkedRef.current = true;
      setSelectedCategory(match);
    }
  }, [deepLinkedCategoryId, categories]);

  return (
    <OuterWrapper>
      <Snackbar
        type="error"
        message={displayError}
        open={!!displayError}
        onClose={() => setDisplayError('')}
        detailedErrors={detailedErrors}
      />
      {deleteCategoryDialog && (
        <Dialog
          actions={[
            {
              ariaLabel: t('generic.cancel'),
              handler: () => {
                setDeleteCategoryDialog(false);
              },
              label: t('generic.cancel'),
              type: 'secondaryOutline',
            },
            {
              ariaLabel: t('backoffice.categories.delete'),
              handler: handleDeleteCategory,
              label: t('backoffice.categories.delete'),
            },
          ]}
          ariaLabel={t('backoffice.categories.deleteTitle')}
          onClose={() => setDeleteCategoryDialog(false)}
          open={deleteCategoryDialog}
          sx={{ '.MuiDialogContent-root': { overflowY: 'unset' } }}
          title={t('backoffice.categories.deleteTitle')}
        >
          <T
            color={theme.palette.mainDark}
            fontWeight="semibold"
            sx={{ textAlign: 'center' }}
            variant="subtitle"
          >
            {t('backoffice.categories.deleteText', {
              name: newSelectTranslation(toDeleteCategory.name, locale),
            })}
          </T>
        </Dialog>
      )}
      <SearchWrapper>
        <SearchField
          ariaLabel={t('backoffice.categories.search')}
          disabled={isLocked}
          loading={isLocked}
          onChange={({ value }) => setSearchValue(value)}
          onClear={() => setSearchValue('')}
          placeholder={t('backoffice.categories.search')}
          theme="dark"
          type="primary"
          value={searchValue}
        />
        <Button
          ariaLabel={t('backoffice.categories.new')}
          disabled={deleteCategoryResponse.loading}
          iconRight={AddIcon}
          onClick={() => setSelectedCategory({})}
          sx={{ flex: '0 0 auto' }}
        >
          {t('backoffice.categories.new')}
        </Button>
      </SearchWrapper>
      <CategoriesList
        categories={categoriesFiltered}
        filter={searchValue}
        handleDeleteCategory={handleDeleteCategoryDialog}
        isLocked={isLocked}
        loading={listCategoriesResponse.loading}
        selectedCategory={selectedCategory}
        setSelectedCategory={setSelectedCategory}
      />
      <CategoryDrawer
        category={selectedCategory}
        handleClose={handleCloseDrawer}
        handleCreate={handleCreateCategory}
        handleCreateEnded={!createCategoryResponse.loading}
        handleUpdate={handleUpdateCategory}
        handleUpdateEnded={!updateCategoryResponse.loading}
        open={drawerOpen}
        showError={showDrawerError}
      />
    </OuterWrapper>
  );
};

export default CategoriesView;
